import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantUsersInstalledVersionsMetricsResponseDtoOutput } from '@/generated/korza-api-client/types.gen';
import { summarizeVersions, type VersionBar } from '@/lib/installed-versions';
import { HelpIconButton } from '@/components/help-panel/help-icon-button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export interface InstalledVersionsCardProps {
  data: TenantUsersInstalledVersionsMetricsResponseDtoOutput | undefined;
}

const BAR_COLORS = ['#155DFC', '#0B348C'];
const OTHERS_COLOR = '#8C94A1';

/**
 * "Versões do app" (ANLT-06 AC 7): the highest installed version (semver) of
 * `usersByFullVersion` with "{x}% dos usuários ativos já estão na {versão},
 * a mais recente.", and bars for the 2 versions with most users plus
 * "Outras" with the rest.
 */
export function InstalledVersionsCard({ data }: InstalledVersionsCardProps) {
  const { LL, locale } = useI18nContext();
  const summary = data ? summarizeVersions(data.usersByFullVersion) : null;
  const percent = (pct: number) =>
    new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(pct / 100);
  const labelOf = (bar: VersionBar) =>
    bar.kind === 'version'
      ? bar.label
      : LL.metricsPage.usersTab.versions.others();

  return (
    <Card
      data-installed-versions
      className="flex flex-col gap-3.5 p-5 max-md:p-4"
    >
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-medium">
          {LL.metricsPage.usersTab.versions.title()}
        </h3>
        <HelpIconButton helpKey="installedVersions" />
      </div>
      {!data ? (
        <Skeleton className="h-32 w-full" />
      ) : !summary ? (
        <p className="text-sm text-muted-foreground">
          {LL.metricsPage.usersTab.versions.empty()}
        </p>
      ) : (
        <>
          {summary.latest ? (
            <p
              data-versions-latest
              className="text-[13px] text-muted-foreground"
            >
              <span className="block text-[26px] font-semibold tracking-[-0.02em] text-foreground tabular-nums">
                {percent(summary.latest.pct)}
              </span>{' '}
              {LL.metricsPage.usersTab.versions.latest({
                version: summary.latest.version,
              })}
            </p>
          ) : null}
          <ul className="mt-2 flex flex-col gap-3.5">
            {summary.bars.map((bar, i) => (
              <li
                key={labelOf(bar)}
                data-version-bar
                className="flex flex-col gap-1.5"
              >
                <span className="flex justify-between gap-2 text-sm">
                  <span className="font-semibold">{labelOf(bar)}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {LL.metricsPage.usersTab.versions.users({
                      count: new Intl.NumberFormat(locale).format(bar.users),
                      pct: percent(bar.pct),
                    })}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className="h-2 overflow-hidden rounded-sm bg-muted"
                >
                  <span
                    className="block h-full"
                    style={{
                      width: `${bar.pct}%`,
                      backgroundColor:
                        bar.kind === 'others'
                          ? OTHERS_COLOR
                          : BAR_COLORS[i % BAR_COLORS.length],
                    }}
                  />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
